// Feb 2026 -- production email verification "please verify" screen.
// Reached automatically when the backend returns
// 403 { code: "email_verification_required" } from any protected endpoint.
// See /app/frontend/src/lib/api.js for the axios interceptor.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function VerifyEmailNeededPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);

  const resend = async () => {
    if (!user?.email) { toast.error("We could not find your account email. Please sign in again."); return; }
    setResending(true);
    try {
      await api.post("/auth/send-verification-email", { email: user.email });
      toast.success("If an account exists for that email, we have sent a new verification link.");
    } catch {
      toast.success("If an account exists for that email, we have sent a new verification link.");
    } finally {
      setResending(false);
    }
  };

  const signOut = async () => {
    try { await logout(); } catch { /* no-op */ }
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen grid place-items-center bg-[#F6F9FE] px-4">
      <div className="w-full max-w-md bg-white rounded-xl border border-slate-200 p-8 shadow-sm"
           data-testid="verify-email-needed-page">
        <h1 className="font-display text-2xl text-[#0B1A36] mb-3"
            data-testid="verify-email-needed-heading">
          Verify your email
        </h1>
        <p className="text-sm text-[#4B5773] mb-2">
          We have sent a verification link to your email
          {user?.email ? (
            <> at <span className="font-medium text-[#0B1A36]">{user.email}</span></>
          ) : null}.
        </p>
        <p className="text-sm text-[#4B5773] mb-6">
          Click the link to activate your account. Not received it? Check your spam folder or resend.
        </p>
        <div className="space-y-2">
          <Button onClick={resend} disabled={resending}
                  className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg"
                  data-testid="verify-email-needed-resend-btn">
            {resending ? "Sending..." : "Resend verification email"}
          </Button>
          <Button onClick={signOut} variant="outline"
                  className="w-full rounded-lg"
                  data-testid="verify-email-needed-signout-btn">
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
